import React from 'react'
import PropTypes from 'prop-types'
import { css } from '@emotion/react'
import { connect } from 'react-redux'

import { doSetFlag, selectIsFetchingFlags, selectFlag } from 'ducks/flags'

import styled from '@emotion/styled'
import { SUI, Icon } from 'shared/ui'
import storage from 'util/storage'
import SpacedItems from 'shared/components/ui/SpacedItems'

const NoticeBarContainer = styled('div')`
  position: relative;
  width: 100%;
  padding: ${props => props.theme.spacing.base};
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  flex-grow: 1;
  color: ${props => props.theme.color.monochrome.white};
  ${props =>
    props.type === 'warning' &&
    `color: ${props.theme.color.monochrome.black};`};
  text-align: center;
  font-size: ${props => props.theme.fontSize.large};
  font-weight: ${props => props.theme.fontWeight.semibold};
  background-color: ${props => props.theme.color.brand['900']};
  ${props =>
    props.type === 'warning' &&
    `background-color: ${props.theme.color.primary.warning};`};
  min-height: 48px;
`

const DismissIcon = styled(Icon)`
  font-size: ${props => props.theme.spacing['10']};
  color: ${props => props.theme.color.monochrome.white};
  ${props =>
    props.type === 'warning' &&
    `color: ${props.theme.color.monochrome.black};`};
  display: flex;
  align-items: center;
  height: auto;
  opacity: 0.5;
  &:hover {
    color: ${props => props.theme.color.monochrome.white};
    ${props =>
      props.type === 'warning' &&
      `color: ${props.theme.color.monochrome.black};`};
    opacity: 1;
  }
`

const noticeFlagTextStyle = () => theme => css`
  margin-right: 8px;
  background-color: ${theme.color.secondary.positive};
  color: ${theme.color.primary.positive};
  border-radius: 2px;
  padding: 1px 6px;
  font-size: ${theme.fontSize.base};
  user-select: none;
`

class NoticeBar extends React.PureComponent {
  // eslint-disable-next-line react/sort-comp
  state = {
    isVisible: false,
  }

  constructor(props) {
    super(props)
    this.possiblyAutomaticallyShow(props)
  }

  componentDidMount() {
    this.mounted = true
    this.possiblyAutomaticallyShow(this.props)
  }

  componentWillReceiveProps(nextProps) {
    this.possiblyAutomaticallyShow(nextProps)
  }

  setIsVisible(isVisible) {
    if (this.mounted) {
      this.setState({ isVisible })
    } else {
      this.state.isVisible = isVisible // eslint-disable-line react/no-direct-mutation-state
    }
  }

  handleFlagAsSeen(flagName, isSessionFlag, flagHasSeen) {
    if (isSessionFlag) {
      storage.set(flagName, true)
      return
    }

    flagHasSeen(flagName)
  }

  hasSeenFlag(flagName, isSessionFlag, hasSeenFromAPI) {
    if (isSessionFlag) return !!storage.get(flagName)

    return hasSeenFromAPI
  }

  possiblyAutomaticallyShow(props) {
    const {
      dismissible,
      flagName,
      isFetchingFlags,
      hasSeenFromAPI,
      flagHasSeen,
      forceShow,
      isSessionFlag,
    } = props

    // not using flags, show it
    if (flagName === undefined || forceShow) {
      this.setIsVisible(true)
    }

    const hasSeen = this.hasSeenFlag(flagName, isSessionFlag, hasSeenFromAPI)

    if (!isFetchingFlags && hasSeen === false) {
      // mark as seen only if its not a dismissible (one-off) notice
      // otherwise will be handled on dismiss
      if (!dismissible) {
        this.handleFlagAsSeen(flagName, isSessionFlag, flagHasSeen)
      }
      this.setIsVisible(true)
    }
  }

  handleDismiss = () => {
    const { flagName, flagHasSeen, isSessionFlag } = this.props
    this.handleFlagAsSeen(flagName, isSessionFlag, flagHasSeen)
    this.setIsVisible(false)
  }

  render() {
    const {
      dismissible,
      hasSeenFromAPI,
      children,
      forceShow,
      noticeFlagText,
      isSessionFlag,
      flagName,
      type,
      ...rest
    } = this.props
    const { isVisible } = this.state

    const hasSeen = this.hasSeenFlag(flagName, isSessionFlag, hasSeenFromAPI)

    if ((hasSeen && !forceShow) || !isVisible) {
      return null
    }

    return (
      <SUI>
        <NoticeBarContainer type={type} {...rest}>
          <SpacedItems.Container
            horizontalAlign="center"
            verticalAlign="center"
            css={css`
              width: 100%;
              flex-grow: 1;
            `}
          >
            <SpacedItems.Item grow={1} shink={0}>
              {noticeFlagText && (
                <span css={noticeFlagTextStyle}>{noticeFlagText}</span>
              )}
              {children}
            </SpacedItems.Item>

            {dismissible && (
              <SpacedItems.Item grow={0} shrink={0}>
                <DismissIcon
                  type={type}
                  name="close"
                  onClick={this.handleDismiss}
                />
              </SpacedItems.Item>
            )}
          </SpacedItems.Container>
        </NoticeBarContainer>
      </SUI>
    )
  }
}

NoticeBar.propTypes = {
  isSessionFlag: PropTypes.bool,
}

NoticeBar.defaultProps = {
  isSessionFlag: false,
}

const select = (initialState, initialOwnProps) => {
  return state => {
    return {
      hasSeenFromAPI: initialOwnProps.flagName
        ? selectFlag(state)(initialOwnProps.flagName)
        : undefined,
      isFetchingFlags: selectIsFetchingFlags(state),
    }
  }
}

const perform = dispatch => {
  return {
    flagHasSeen: flagName => dispatch(doSetFlag(flagName)),
  }
}

export default connect(select, perform)(NoticeBar)
